import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const vendaInicial = {
  estoqueAgricolaId: '',
  clienteId: '',
  dataVenda: new Date().toISOString().substring(0, 10),
  dataRecebimento: new Date().toISOString().substring(0, 10),
  quantidade: '',
  valorUnitario: '',
  formaPagamento: 1,
  emitirNotaFiscal: false,
  observacao: ''
};

const notaInicial = {
  agricultorId: '',
  ambiente: 'Producao',
  descricao: '',
  modFrete: 9
};
export default function ProducaoAgricola() {
  const navigate = useNavigate();
  const [estoques, setEstoques] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [venda, setVenda] = useState(vendaInicial);
  const [nota, setNota] = useState(notaInicial);
  const [vendaSelecionada, setVendaSelecionada] = useState(null);
  const [loading, setLoading] = useState(false);

  const estoqueSelecionado = useMemo(
    () => estoques.find(x => String(x.id) === String(venda.estoqueAgricolaId)),
    [estoques, venda.estoqueAgricolaId]
  );

  const totalVenda = useMemo(() => {
    const quantidade = Number(venda.quantidade || 0);
    const valorUnitario = Number(venda.valorUnitario || 0);
    return quantidade * valorUnitario;
  }, [venda.quantidade, venda.valorUnitario]);

  const carregar = async () => {
    setLoading(true);
    try {
      const [estoquesRes, vendasRes, clientesRes, movRes] = await Promise.all([
        api.get('/producao-agricola/estoques'),
        api.get('/producao-agricola/vendas'),
        api.get('/cliente'),
        api.get('/producao-agricola/movimentacoes')
      ]);

      setEstoques(Array.isArray(estoquesRes.data) ? estoquesRes.data : []);
      setVendas(Array.isArray(vendasRes.data) ? vendasRes.data : []);
      setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      setMovimentacoes(Array.isArray(movRes.data) ? movRes.data : []);
    } catch {
      showError('Erro ao carregar produção agrícola.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const registrarVenda = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/producao-agricola/vendas', {
        ...venda,
        estoqueAgricolaId: Number(venda.estoqueAgricolaId),
        clienteId: Number(venda.clienteId),
        quantidade: Number(venda.quantidade),
        valorUnitario: Number(venda.valorUnitario),
        formaPagamento: Number(venda.formaPagamento)
      });
      showSuccess('Venda registrada, estoque baixado e conta a receber gerada.');
      setVenda(vendaInicial);
      await carregar();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao registrar venda da produção.');
    } finally {
      setLoading(false);
    }
  };

  const emitirNota = async (e) => {
    e.preventDefault();
    if (!vendaSelecionada) return;
    setLoading(true);

    try {
      await api.post(`/producao-agricola/vendas/${vendaSelecionada.id}/emitir-nfe`, {
        ...nota,
        agricultorId: Number(nota.agricultorId),
        modFrete: Number(nota.modFrete)
      });
      showSuccess('NF-e enviada pela API fiscal e vinculada à venda.');
      setVendaSelecionada(null);
      setNota(notaInicial);
      await carregar();
    } catch (err) {
      const mensagem = err.response?.data?.message || 'Erro ao emitir NF-e da venda.';
      showError(mensagem);
      if (mensagem.includes('configuração fiscal incompleta')) {
        const produto = estoques.find(x => String(x.id) === String(vendaSelecionada.estoqueAgricolaId));
        if (produto) navigate(`/producao-agricola/produtos/${produto.id}/fiscal`);
      }
    } finally {
      setLoading(false);
    }
  };

  const estoqueColumns = [
    { name: 'Produto', selector: row => row.produto, sortable: true },
    { name: 'Safra', selector: row => row.safraNome || '-', sortable: true },
    { name: 'Talhão', selector: row => row.talhaoNome || '-', sortable: true },
    { name: 'Saldo', selector: row => `${Number(row.quantidadeDisponivel || 0).toLocaleString('pt-BR')} ${row.unidade}`, sortable: true },
    { name: 'Armazém', selector: row => row.armazem, sortable: true },
    { name: 'Valor médio', selector: row => Number(row.valorMedioEstimado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), sortable: true },
    { name: 'Fiscal', cell: row => <button className={`btn btn-sm ${row.configuracaoFiscalCompleta ? 'btn-outline-success' : 'btn-outline-warning'}`} onClick={() => navigate(`/producao-agricola/produtos/${row.id}/fiscal`)}><i className="bi bi-receipt"/> {row.configuracaoFiscalCompleta ? 'Configurado' : 'Configurar'}</button>, ignoreRowClick:true }
  ];

  const vendaColumns = [
    { name: 'Data', selector: row => new Date(row.dataVenda).toLocaleDateString(), sortable: true },
    { name: 'Produto', selector: row => row.produto, sortable: true },
    { name: 'Cliente', selector: row => row.clienteNome, sortable: true },
    { name: 'Quantidade', selector: row => Number(row.quantidade || 0).toLocaleString('pt-BR'), sortable: true },
    { name: 'Total', selector: row => Number(row.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), sortable: true },
    { name: 'Receita', selector: row => row.receitaId ? `#${row.receitaId}` : '-', sortable: true },
    { name: 'NF-e', selector: row => row.notaFiscalId ? `#${row.notaFiscalId}` : (row.emitirNotaFiscal ? 'Pendente' : 'Não emitir'), sortable: true },
    {
      name: 'Ações',
      cell: row => (
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          disabled={!row.emitirNotaFiscal || row.notaFiscalId}
          onClick={() => setVendaSelecionada(row)}
        >
          <i className="bi bi-receipt"></i> Emitir
        </button>
      ),
      ignoreRowClick: true
    }
  ];

  const movimentoColumns = [
    { name: 'Data', selector: row => new Date(row.dataMovimento).toLocaleDateString(), sortable: true },
    { name: 'Tipo', selector: row => row.tipo, sortable: true },
    { name: 'Quantidade', selector: row => Number(row.quantidade || 0).toLocaleString('pt-BR'), sortable: true },
    { name: 'Saldo', selector: row => Number(row.saldoAposMovimento || 0).toLocaleString('pt-BR'), sortable: true },
    { name: 'Origem', selector: row => row.colheitaId ? `Colheita #${row.colheitaId}` : row.vendaProducaoId ? `Venda #${row.vendaProducaoId}` : '-', sortable: true },
    { name: 'Observação', selector: row => row.observacao || '-' }
  ];

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3 mb-4">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success" />
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">Estoque agrícola e venda da produção</h5>
              <small className="text-white-50">Colheita, saldo, venda, financeiro e fiscal no mesmo fluxo</small>
            </div>
          </div>
          <button className="btn btn-light text-success fw-semibold" onClick={carregar} disabled={loading}>
            <i className="bi bi-arrow-clockwise"></i> Atualizar
          </button>
        </div>
        <div className="card-body">
          <DataTable
            columns={estoqueColumns}
            data={estoques}
            pagination
            dense
            striped
            highlightOnHover
            progressPending={loading}
            noDataComponent={<div className="text-muted">Nenhum estoque agrícola encontrado. Lance uma colheita primeiro.</div>}
          />
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-header bg-light">
          <strong>Registrar venda da produção</strong>
        </div>
        <div className="card-body">
          <form onSubmit={registrarVenda} className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Estoque *</label>
              <select className="form-select" name="estoqueAgricolaId" value={venda.estoqueAgricolaId} onChange={e => setVenda(prev => ({ ...prev, estoqueAgricolaId: e.target.value }))} required disabled={loading}>
                <option value="">Selecione...</option>
                {estoques.filter(e => Number(e.quantidadeDisponivel) > 0).map(e => (
                  <option key={e.id} value={e.id}>
                    {e.produto} - {Number(e.quantidadeDisponivel).toLocaleString('pt-BR')} {e.unidade} - {e.armazem}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Cliente *</label>
              <select className="form-select" name="clienteId" value={venda.clienteId} onChange={e => setVenda(prev => ({ ...prev, clienteId: e.target.value }))} required disabled={loading}>
                <option value="">Selecione...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nome} - {c.documento}</option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Venda *</label>
              <input type="date" className="form-control" value={venda.dataVenda} onChange={e => setVenda(prev => ({ ...prev, dataVenda: e.target.value }))} required disabled={loading} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Recebimento *</label>
              <input type="date" className="form-control" value={venda.dataRecebimento} onChange={e => setVenda(prev => ({ ...prev, dataRecebimento: e.target.value }))} required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Quantidade *</label>
              <input type="number" step="0.0001" className="form-control" value={venda.quantidade} max={estoqueSelecionado?.quantidadeDisponivel || undefined} onChange={e => setVenda(prev => ({ ...prev, quantidade: e.target.value }))} required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Valor unitário *</label>
              <CurrencyInput className="form-control" value={venda.valorUnitario} onValueChange={value => setVenda(prev => ({ ...prev, valorUnitario: value }))} placeholder="0,00" required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Forma pagamento</label>
              <select className="form-select" value={venda.formaPagamento} onChange={e => setVenda(prev => ({ ...prev, formaPagamento: e.target.value }))} disabled={loading}>
                <option value={0}>Dinheiro</option>
                <option value={1}>Pix</option>
                <option value={2}>Cartão de crédito</option>
                <option value={3}>Cartão de débito</option>
                <option value={4}>Transferência</option>
                <option value={5}>Boleto</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Total</label>
              <input className="form-control" value={totalVenda.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} readOnly />
            </div>

            <div className="col-md-9">
              <label className="form-label fw-semibold">Observação</label>
              <input className="form-control" value={venda.observacao} onChange={e => setVenda(prev => ({ ...prev, observacao: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check">
                <input className="form-check-input" type="checkbox" checked={venda.emitirNotaFiscal} onChange={e => setVenda(prev => ({ ...prev, emitirNotaFiscal: e.target.checked }))} id="emitirNotaFiscal" />
                <label className="form-check-label" htmlFor="emitirNotaFiscal">Emitir NF-e</label>
              </div>
            </div>

            <div className="col-12 d-flex justify-content-end">
              <button className="btn btn-success" disabled={loading}>
                <i className="bi bi-cart-check"></i> Registrar venda
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-header bg-light">
          <strong>Vendas registradas</strong>
        </div>
        <div className="card-body">
          <DataTable columns={vendaColumns} data={vendas} pagination striped highlightOnHover progressPending={loading} />
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-header bg-light">
          <strong>Movimentações recentes</strong>
        </div>
        <div className="card-body">
          <DataTable columns={movimentoColumns} data={movimentacoes} pagination dense striped highlightOnHover progressPending={loading} />
        </div>
      </div>

      {vendaSelecionada && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.35)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={emitirNota}>
                <div className="modal-header">
                  <h5 className="modal-title">Emitir NF-e da venda #{vendaSelecionada.id}</h5>
                  <button type="button" className="btn-close" onClick={() => setVendaSelecionada(null)} />
                </div>
                <div className="modal-body row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Agricultor emitente *</label>
                    <input type="number" className="form-control" value={nota.agricultorId} onChange={e => setNota(prev => ({ ...prev, agricultorId: e.target.value }))} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Ambiente</label>
                    <select className="form-select" value={nota.ambiente} onChange={e => setNota(prev => ({ ...prev, ambiente: e.target.value }))}>
                      <option value="Producao">Produção</option>
                      <option value="Homologacao">Homologação</option>
                    </select>
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">Descrição do item</label>
                    <input className="form-control" value={nota.descricao} onChange={e => setNota(prev => ({ ...prev, descricao: e.target.value }))} placeholder={vendaSelecionada.produto} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Frete</label>
                    <select className="form-select" value={nota.modFrete} onChange={e => setNota(prev => ({ ...prev, modFrete: e.target.value }))}>
                      <option value={9}>Sem frete</option>
                      <option value={0}>Por conta emitente</option>
                      <option value={1}>Por conta destinatário</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setVendaSelecionada(null)}>Cancelar</button>
                  <button className="btn btn-success" disabled={loading}>
                    <i className="bi bi-send"></i> Enviar NF-e
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
